/**
 * Task status banner — V4 D-2.
 *
 * The founder finished a task and could not tell it had finished. The status
 * lived in a small select in the side panel; everything else on the page —
 * title, description, checklist — looked exactly as it had a moment before.
 *
 * So a terminal or blocked state now announces itself at full width, above the
 * title, before you read anything else. The priority spine stays where it is;
 * this is the landing signal, not a replacement for it.
 */

import { Ban, CheckCircle2, RotateCcw, XCircle } from 'lucide-react';
import type { StatusCategory } from '@virtuetasks/types';
import { Button } from '@/components/ui/button';
import type { TimelineRow } from './api';

interface BannerProps {
  category: StatusCategory;
  statusName: string;
  completedAt?: string;
  blockedReason?: string;
  /** Read to name who moved it, and to find the cancellation reason. */
  timeline: TimelineRow[];
  canReopen: boolean;
  onReopen: () => void;
}

/** `12 Mar 2026` — long enough to be unambiguous, short enough for one line. */
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Who last moved this task into its current status, from the activity feed.
 *
 * `completedAt` records *when* but not *by whom*, and adding an actor column
 * to every task is a migration for one line of copy. A status change is logged
 * as `task.updated` carrying a `statusId` change — there is no dedicated
 * action — so that is what this looks for.
 */
function lastStatusActor(timeline: TimelineRow[]): string | null {
  for (const row of timeline) {
    if (row.type !== 'activity') continue;
    const payload = row.payload as {
      actorName?: string;
      changes?: { field: string }[];
    };
    if (!payload.changes?.some((c) => c.field === 'statusId')) continue;
    return payload.actorName ?? null;
  }
  return null;
}

/**
 * What the status changed *to*, as recorded on the most recent status change.
 * Used for the cancellation note: `cancelled` is a `requiresReason` status, so
 * a reason was collected, and it is the one useful thing to show.
 */
function lastStatusChangeNote(timeline: TimelineRow[]): string | null {
  for (const row of timeline) {
    if (row.type !== 'activity') continue;
    const payload = row.payload as {
      changes?: { field: string; to?: unknown }[];
      metadata?: { reason?: string };
    };
    if (!payload.changes?.some((c) => c.field === 'statusId')) continue;
    return payload.metadata?.reason ?? null;
  }
  return null;
}

export function StatusBanner({
  category,
  statusName,
  completedAt,
  blockedReason,
  timeline,
  canReopen,
  onReopen,
}: BannerProps) {
  const blocked = Boolean(blockedReason);

  // Blocked outranks done: a blocked task that is somehow also in a done
  // column is a problem to surface, not to hide behind a green bar.
  if (blocked) {
    return (
      <div
        role="status"
        data-testid="status-banner"
        data-variant="blocked"
        className="flex items-center gap-2 rounded-[8px] border border-[var(--danger)]/40 bg-[var(--danger)]/10 px-3 py-2.5 text-[13px] text-[var(--danger)]"
      >
        <Ban className="size-4 shrink-0" />
        <span className="font-medium">Blocked</span>
        <span className="min-w-0 truncate opacity-90">— {blockedReason}</span>
      </div>
    );
  }

  if (category === 'done') {
    const actor = lastStatusActor(timeline);
    return (
      <div
        role="status"
        data-testid="status-banner"
        data-variant="done"
        className="flex items-center gap-2 rounded-[8px] border border-[var(--accent)]/40 bg-[var(--accent-soft)] px-3 py-2.5 text-[13px] text-[var(--accent)]"
      >
        <CheckCircle2 className="size-4 shrink-0" />
        <span className="font-medium">{statusName}</span>
        <span className="min-w-0 truncate opacity-90">
          {completedAt ? `— completed ${formatDay(completedAt)}` : '— completed'}
          {actor ? ` by ${actor}` : ''}
        </span>
        {canReopen && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto shrink-0"
            data-testid="reopen-task"
            onClick={onReopen}
          >
            <RotateCcw />
            Reopen
          </Button>
        )}
      </div>
    );
  }

  if (category === 'cancelled') {
    const reason = lastStatusChangeNote(timeline);
    return (
      <div
        role="status"
        data-testid="status-banner"
        data-variant="cancelled"
        className="flex items-center gap-2 rounded-[8px] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2.5 text-[13px] vt-muted"
      >
        <XCircle className="size-4 shrink-0" />
        <span className="font-medium">{statusName}</span>
        {reason && <span className="min-w-0 truncate">— {reason}</span>}
        {canReopen && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto shrink-0"
            data-testid="reopen-task"
            onClick={onReopen}
          >
            <RotateCcw />
            Reopen
          </Button>
        )}
      </div>
    );
  }

  return null;
}
